/**
 * FINANCIAL RUNWAY — business-wide cash position
 *
 * Bank balance minus what is already committed this month (fixed costs) minus
 * what the tenants are on pace to spend before their cycles close.
 *
 * Reads GET /admin/financials/snapshot. Every dollar on this page comes from
 * cost_events.total_cost summed in SQL — see finance_cost_rollup /
 * get_financial_snapshot. Nothing here recomputes cost client-side.
 *
 * Three states this page must never fake, because acting on a wrong one costs
 * real money:
 *   - bank balance unavailable  -> say so, with the reason. Never render $0.
 *   - net position undefined    -> say so. Never render $0.
 *   - projection not applicable -> show a dash. A custom date range has no
 *                                  "rest of the period" to project into.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetricCard from '../components/admin/financials/MetricCard';
import FinancialNav from '../components/admin/financials/FinancialNav';
import TenantSelector from '../components/admin/financials/TenantSelector';
import ManageCostsModal from '../components/admin/financials/ManageCostsModal';
import {
  Landmark, DollarSign, TrendingUp, Repeat, Calendar, Settings,
  RefreshCw, AlertCircle, AlertTriangle, MessageSquare, Cpu
} from 'lucide-react';
import apiClient from '../lib/apiClient';

const money = (n, dp = 2) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—';

export default function FinancialRunway() {
  const [loading, setLoading] = useState(true);
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);

  const [view, setView] = useState('cycle');       // 'cycle' | 'range'
  const [dateRange, setDateRange] = useState('30d');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [manageOpen, setManageOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSnapshot(); }, [view, dateRange, tenantFilter]);

  const getDateRange = (range) => {
    const today = new Date();
    const to = today.toLocaleDateString('en-CA');
    const back = (days) => {
      const d = new Date(today); d.setDate(d.getDate() - days);
      return d.toLocaleDateString('en-CA');
    };
    switch (range) {
      case '7d':  return { from: back(7),  to };
      case '90d': return { from: back(90), to };
      case 'ytd': return { from: new Date(today.getFullYear(), 0, 1).toLocaleDateString('en-CA'), to };
      default:    return { from: back(30), to };
    }
  };

  const loadSnapshot = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ view });
      if (view === 'range') {
        const d = getDateRange(dateRange);
        params.append('date_from', d.from);
        params.append('date_to', d.to);
      }
      if (tenantFilter !== 'all') params.append('tenant_id', tenantFilter);

      const response = await apiClient.get(`/admin/financials/snapshot?${params}`);
      if (response.data?.error) throw new Error(response.data.error);
      setSnap(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load runway data');
    } finally {
      setLoading(false);
    }
  };

  const isCycle = view === 'cycle';
  const mercuryOk = snap?.mercury?.ok === true;
  const safe = snap?.safe_to_spend;
  const safeOk = safe?.complete === true && safe?.value !== null && safe?.value !== undefined;
  const tenants = snap?.tenants || [];   // already sorted by cost_to_date desc server-side
  const unpriced = snap?.fixed_costs?.unpriced || [];
  const balances = snap?.provider_balances?.items || [];
  const staleBalances = balances.filter(b => b.stale);
  const inTransit = snap?.in_transit;
  const stripeBalance = snap?.stripe_balance;
  const cap = safe?.at_plan_cap;

  const twilioTotal = snap?.totals?.twilio_cost || 0;
  const aiTotal = snap?.totals?.ai_cost || 0;
  const catTotal = twilioTotal + aiTotal;
  const pct = (part) => (catTotal > 0 ? (part / catTotal) * 100 : 0);

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              🛟 Runway
            </h1>
            <p className="text-gray-600 mt-2">
              Cash position: bank balance against committed fixed costs and projected variable spend
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setManageOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              <Settings className="w-4 h-4" />
              Manage Costs
            </button>
            <button
              onClick={loadSnapshot}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-6 items-center p-4 bg-white rounded-lg shadow-sm flex-wrap">
          <div className="flex gap-2 items-center">
            <Calendar className="w-5 h-5 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">View:</label>
            <select
              value={view}
              onChange={(e) => setView(e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="cycle">Billing Cycle (per tenant)</option>
              <option value="range">Custom Date Range</option>
            </select>
          </div>

          {/* Same Time Range control the other financial pages use, shown only
              when it actually applies. */}
          {!isCycle && (
            <div className="flex gap-2 items-center">
              <label className="text-sm font-medium text-gray-700">Time Range:</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="ytd">Year to Date</option>
              </select>
            </div>
          )}

          <TenantSelector value={tenantFilter} onChange={setTenantFilter} />
        </div>
      </div>

      <FinancialNav />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Failed to load runway data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button onClick={loadSnapshot} className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">
              Try again →
            </button>
          </div>
        </div>
      )}

      {loading && !snap ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading runway…</p>
          <p className="text-sm text-gray-500 mt-1">Aggregating cost events and fetching balance</p>
        </div>
      ) : (
        <div className={loading ? 'opacity-50 pointer-events-none' : ''}>

          {/* ── Bank balance: never render a fake zero ───────────────────── */}
          {!mercuryOk && (
            <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-900">Bank balance unavailable</h3>
                <p className="text-sm text-amber-800 mt-1">
                  {snap?.mercury?.error || 'Mercury did not return a balance.'}
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  Every cost figure below is still accurate. Only the balance and net position are missing —
                  they are shown as unavailable rather than as $0.
                </p>
              </div>
            </div>
          )}

          {/* ── SAFE TO SPEND — the headline number ──────────────────────── */}
          <div className="mb-8">
            {safeOk ? (
              <div className={`rounded-lg shadow-lg p-6 border-2 ${
                safe.value >= 0 ? 'bg-white border-green-500' : 'bg-white border-red-500'
              }`}>
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">💰 Safe to Spend</p>
                    <div className="flex items-end gap-4 flex-wrap mt-2">
                      <div>
                        <p className={`text-5xl font-bold ${safe.value >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {money(safe.value)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">if usage stays flat</p>
                      </div>
                      {/* Both ends of the range, side by side and always
                          visible. A single figure that assumes nobody grows is
                          the optimistic end, not the answer. */}
                      {cap?.available && (
                        <>
                          <span className="text-3xl text-gray-300 pb-6">·</span>
                          <div>
                            <p className={`text-4xl font-bold ${cap.value >= 0 ? 'text-amber-600' : 'text-red-700'}`}>
                              {cap.complete ? '' : '≥ '}{money(cap.value)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              if all tenants hit their caps
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-gray-600 mt-3">
                      What's left after this month's bills and expected customer usage
                    </p>
                    {cap?.available && !cap.complete && (
                      <p className="text-xs text-amber-700 mt-1">{cap.reason}</p>
                    )}
                    {!cap?.available && cap?.reason && (
                      <p className="text-xs text-gray-500 mt-1">Cap scenario unavailable: {cap.reason}</p>
                    )}
                  </div>

                  {/* In transit sits BESIDE the number, never inside it. */}
                  <div className="text-right">
                    {/* Two independent "money coming" signals. A Payout object
                        only exists once Stripe creates it, so an upcoming
                        payout the Dashboard is merely projecting has no object
                        behind it yet — in that window the balance is the only
                        signal. Show whichever exists rather than claiming
                        nothing is coming. */}
                    {inTransit?.ok && inTransit.count > 0 ? (
                      <>
                        <p className="text-sm font-medium text-gray-500">Not counted above</p>
                        <p className="text-2xl font-semibold text-blue-600 mt-1">
                          + {money(inTransit.total)} in transit
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {inTransit.count} Stripe payout{inTransit.count === 1 ? '' : 's'}
                          {inTransit.earliest_arrival && <> · expected {shortDate(inTransit.earliest_arrival)}</>}
                        </p>
                      </>
                    ) : stripeBalance?.ok && (stripeBalance.pending > 0 || stripeBalance.available > 0) ? (
                      <>
                        <p className="text-sm font-medium text-gray-500">Not counted above</p>
                        <p className="text-2xl font-semibold text-blue-600 mt-1">
                          + {money((stripeBalance.pending || 0) + (stripeBalance.available || 0))} in Stripe
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {money(stripeBalance.available)} available · {money(stripeBalance.pending)} pending
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">No payout object created yet</p>
                      </>
                    ) : inTransit?.ok && stripeBalance?.ok ? (
                      <p className="text-sm text-gray-400">Nothing in transit and no Stripe balance</p>
                    ) : (
                      <p className="text-sm text-amber-600 max-w-xs">
                        Stripe unavailable: {inTransit?.error || stripeBalance?.error}
                      </p>
                    )}
                  </div>
                </div>

                {/* Every input, spelled out, so the headline is auditable */}
                <div className="mt-5 pt-4 border-t border-gray-200 text-sm text-gray-600 flex flex-wrap gap-x-2 gap-y-1">
                  <span>{money(safe.components.mercury_available)} in Mercury</span>
                  <span className="text-gray-400">+</span>
                  <span>{money(safe.components.prepaid_credit)} prepaid credit</span>
                  <span className="text-gray-400">−</span>
                  <span>{money(safe.components.fixed_costs_remaining)} fixed costs left</span>
                  <span className="text-gray-400">−</span>
                  <span>{money(safe.components.projected_variable_remaining)} projected usage</span>
                  <span className="text-gray-400">=</span>
                  <span className={`font-semibold ${safe.value >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {money(safe.value)}
                  </span>
                  <button
                    onClick={() => setManageOpen(true)}
                    className="ml-auto text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <Settings className="w-3.5 h-3.5" /> Manage Costs
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg shadow-lg p-6 border-2 border-amber-300 bg-white">
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">💰 Safe to Spend</p>
                <p className="text-5xl font-bold mt-2 text-amber-600">Unavailable</p>
                <p className="text-gray-600 mt-2">
                  What's left after this month's bills and expected customer usage
                </p>
                <p className="text-sm text-amber-700 mt-3">{safe?.reason}</p>
              </div>
            )}
          </div>

          {/* ── Supporting cards ─────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Mercury */}
            {mercuryOk ? (
              <MetricCard
                label="🏦 Mercury Balance"
                value={snap.mercury.available_balance}
                format="currency"
                icon={<Landmark className="w-6 h-6 text-blue-600" />}
                isLoading={loading}
              />
            ) : (
              <UnavailableCard
                label="🏦 Mercury Balance"
                icon={<Landmark className="w-6 h-6 text-amber-600" />}
                reason={snap?.mercury?.error}
              />
            )}

            <MetricCard
              label={isCycle ? '💵 Spend This Cycle' : '💵 Spend In Range'}
              value={snap?.totals?.cost_to_date || 0}
              format="currency"
              icon={<DollarSign className="w-6 h-6 text-blue-600" />}
              isLoading={loading}
            />

            {snap?.totals?.projected_cycle_total !== null && snap?.totals?.projected_cycle_total !== undefined ? (
              <MetricCard
                label="📈 Projected By Cycle End"
                value={snap.totals.projected_cycle_total}
                format="currency"
                icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
                isLoading={loading}
              />
            ) : (
              <UnavailableCard
                label="📈 Projected By Cycle End"
                icon={<TrendingUp className="w-6 h-6 text-gray-400" />}
                reason="Projections apply to billing cycles only. Switch View to Billing Cycle."
              />
            )}

            <MetricCard
              label="🔁 Fixed Costs Left This Month"
              value={snap?.fixed_costs?.remaining_this_month || 0}
              format="currency"
              icon={<Repeat className="w-6 h-6 text-yellow-600" />}
              isLoading={loading}
            />
          </div>

          {/* ── Provider prepaid balances ─────────────────────────────────── */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-gray-900">💳 Prepaid Provider Credit</h3>
              <span className="text-2xl font-bold text-gray-900">{money(snap?.provider_balances?.total)}</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Money already paid to these providers but not yet used up. Counted as available,
              because Mercury's balance is already net of it.
            </p>

            {staleBalances.length > 0 && (
              <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {staleBalances.map(b => b.provider).join(', ')} last updated over {snap.provider_balances.items[0]?.stale_after_days ?? 7} days ago.
                  A stale balance <strong>usually overstates</strong> Safe to Spend, because consumption is continuous
                  and drains it between updates. Right after an auto-reload it does the opposite and understates,
                  since Mercury has already paid for credit this figure does not yet show. Either way it is wrong —
                  check the console and update below.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {balances.map(b => (
                <ProviderBalanceRow key={b.id} row={b} onSaved={loadSnapshot} />
              ))}
              {balances.length === 0 && (
                <p className="text-sm text-gray-500">No provider balances configured.</p>
              )}
            </div>
          </div>

          {mercuryOk && snap.mercury.excluded_account_count > 0 && (
            <p className="mb-8 text-xs text-gray-500">
              {snap.mercury.excluded_account_count} non-Mercury account(s) excluded from the balance
              (external / recipient accounts are counterparties, not our cash).
            </p>
          )}

          {/* Unpriced fixed costs — a skipped row must be visible */}
          {unpriced.length > 0 && (
            <div className="mb-8 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">
                  {unpriced.length} fixed cost{unpriced.length > 1 ? 's' : ''} excluded from the total
                </h3>
                <p className="text-sm text-amber-800 mt-1">
                  Unrecognised billing frequency — not assumed to be monthly, because guessing would move
                  the runway number silently.
                </p>
                <ul className="text-sm text-amber-800 mt-2 list-disc list-inside">
                  {unpriced.map(u => (
                    <li key={u.id}>{u.description} — frequency <code className="bg-amber-100 px-1 rounded">{String(u.billing_frequency)}</code></li>
                  ))}
                </ul>
                <Link to="/admin/financials/rates" className="text-sm text-amber-900 underline mt-2 inline-block">
                  Fix in Rate Management →
                </Link>
              </div>
            </div>
          )}

          {/* ── Cost category split ──────────────────────────────────────── */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">📊 Cost Category Split</h3>
            <p className="text-sm text-gray-500 mb-5">
              Twilio and AI move independently — a cheap-model migration lowers AI cost without touching SMS.
            </p>

            <div className="flex h-4 rounded-full overflow-hidden bg-gray-100 mb-4">
              <div className="bg-blue-500 h-full" style={{ width: `${pct(twilioTotal)}%` }} title={`Twilio ${money(twilioTotal)}`} />
              <div className="bg-purple-500 h-full" style={{ width: `${pct(aiTotal)}%` }} title={`AI ${money(aiTotal)}`} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <CategoryStat
                icon={<MessageSquare className="w-4 h-4 text-blue-600" />}
                label="Twilio (SMS)" value={twilioTotal} pct={pct(twilioTotal)} dot="bg-blue-500"
              />
              <CategoryStat
                icon={<Cpu className="w-4 h-4 text-purple-600" />}
                label="AI (Anthropic + OpenAI)" value={aiTotal} pct={pct(aiTotal)} dot="bg-purple-500"
              />
              <CategoryStat
                icon={<Repeat className="w-4 h-4 text-yellow-600" />}
                label="Fixed (per month)" value={snap?.fixed_costs?.monthly_total || 0} pct={null} dot="bg-yellow-500"
              />
            </div>
          </div>

          {/* ── Per-tenant table ─────────────────────────────────────────── */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">🏢 Per-Tenant Runway</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {isCycle
                    ? 'Each tenant on its own billing cycle, anchored on billing_cycle_start_date.'
                    : `Shared window ${snap?.range?.date_from} → ${snap?.range?.date_to}. Projections do not apply.`}
                </p>
              </div>
              <span className="text-sm text-gray-500">{tenants.length} tenant{tenants.length === 1 ? '' : 's'}</span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Tenant</Th>
                    <Th right>Cost to Date</Th>
                    <Th right>Twilio</Th>
                    <Th right>AI</Th>
                    <Th right>Daily Pace</Th>
                    <Th right>Projected<br /><span className="normal-case font-normal">current pace</span></Th>
                    <Th right>Up To<br /><span className="normal-case font-normal">if cap reached</span></Th>
                    <Th right>Plan Usage</Th>
                    <Th>Cycle</Th>
                    <Th right>Days Left</Th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tenants.length === 0 && (
                    <tr><td colSpan={10} className="px-6 py-10 text-center text-gray-500">No tenants match this filter.</td></tr>
                  )}
                  {tenants.map((t) => {
                    // Zero-spend tenants stay visible on purpose: "no spend" and
                    // "no data" are different facts and must look different.
                    const zero = t.event_count === 0;
                    return (
                      <tr key={t.tenant_id} className={`hover:bg-gray-50 ${zero ? 'text-gray-400' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/admin/financials/customers/${t.tenant_id}`}
                            className={`font-medium ${zero ? 'text-gray-500' : 'text-blue-600'} hover:underline`}
                          >
                            {t.name || t.tenant_id.slice(0, 8)}
                          </Link>
                          <div className="flex gap-1 mt-1">
                            {t.plan && <Badge>{t.plan}</Badge>}
                            {t.subscription_status && t.subscription_status !== 'active' && (
                              <Badge tone="red">{t.subscription_status}</Badge>
                            )}
                            {(t.is_demo || t.is_test) && <Badge tone="gray">{t.is_demo ? 'demo' : 'test'}</Badge>}
                            {zero && <Badge tone="gray">no spend</Badge>}
                          </div>
                        </td>
                        <Td right strong>{money(t.cost_to_date)}</Td>
                        <Td right>{money(t.twilio_cost)}</Td>
                        <Td right>{money(t.ai_cost)}</Td>
                        <Td right>{t.pace_per_day === null ? <Dash /> : `${money(t.pace_per_day)}/d`}</Td>
                        <Td right strong>
                          {t.projected_cycle_total === null ? <Dash /> : (
                            <>
                              {money(t.projected_cycle_total)}
                              {/* The straight-line pace can exceed what the plan cap physically
                                  allows to be sent. The API clamps it; say so here rather than
                                  quietly showing a different number than the raw trend. */}
                              {t.pace_clamped_by_cap && (
                                <div
                                  className="text-xs font-normal text-gray-400"
                                  title={`Straight-line pace projected ${money(t.cost_to_date + t.projected_remaining_unclamped)} for this cycle, which would need more messages than the plan cap allows. Capped to the plan limit, which is enforced at send time.`}
                                >
                                  capped
                                </div>
                              )}
                            </>
                          )}
                        </Td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold">
                          {t.plan_cap?.projected_cycle_total_at_cap === null ? (
                            <span className="text-gray-300 font-normal" title={t.plan_cap?.unavailable_reason || 'Not available'}>—</span>
                          ) : (
                            <span className="text-amber-700">{money(t.plan_cap.projected_cycle_total_at_cap)}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                          {t.plan_cap?.limit === null ? <Dash /> : (
                            <>
                              <div>{t.plan_cap.used.toLocaleString()} / {t.plan_cap.limit.toLocaleString()}</div>
                              <div className="text-xs text-gray-400">
                                {t.plan_cap.pct_used === null ? '' : `${t.plan_cap.pct_used}% used`}
                                {t.plan_cap.counter_window_mismatch && (
                                  <span className="text-amber-600" title={t.plan_cap.unavailable_reason}> ⚠</span>
                                )}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {t.cycle?.complete
                            ? <>{shortDate(t.cycle.start)} → {shortDate(t.cycle.end)}</>
                            : <span className="text-amber-600">{t.cycle?.reason || 'no cycle'}</span>}
                        </td>
                        <Td right>
                          {t.cycle?.days_remaining === null || t.cycle?.days_remaining === undefined
                            ? <Dash />
                            : `${Math.floor(t.cycle.days_remaining)}d`}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
                {tenants.length > 0 && (
                  <tfoot className="bg-gray-50 font-semibold text-gray-900">
                    <tr>
                      <td className="px-6 py-3 text-sm">Total</td>
                      <td className="px-6 py-3 text-sm text-right">{money(snap?.totals?.cost_to_date)}</td>
                      <td className="px-6 py-3 text-sm text-right">{money(twilioTotal)}</td>
                      <td className="px-6 py-3 text-sm text-right">{money(aiTotal)}</td>
                      <td className="px-6 py-3 text-sm text-right"></td>
                      <td className="px-6 py-3 text-sm text-right">
                        {snap?.totals?.projected_cycle_total === null ? <Dash /> : money(snap?.totals?.projected_cycle_total)}
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-amber-700">
                        {snap?.totals?.projected_cycle_total_at_cap === null ? <Dash /> : (
                          <>{snap?.totals?.at_cap_is_complete ? '' : '≥ '}
                          {money(snap?.totals?.projected_cycle_total_at_cap)}</>
                        )}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            Snapshot as of {snap?.as_of ? new Date(snap.as_of).toLocaleString() : '—'}.
            Costs summed from cost_events in SQL. Cycles anchored on billing_cycle_start_date.
          </p>
        </div>
      )}

      {/* Stays on the Runway tab — no navigation. Refetches the snapshot on
          close so Safe to Spend reflects the edits immediately. */}
      <ManageCostsModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onSaved={loadSnapshot}
        balances={balances}
      />
    </div>
  );
}

/**
 * One provider's prepaid balance, editable in place.
 *
 * A live-sourced row (Twilio, when ISV parent creds are present) shows its
 * balance read-only — typing over a number the API just returned would be
 * overwritten on the next refresh anyway. Auto-reload settings stay editable
 * for BOTH providers, because neither exposes them over an API at all.
 */
function ProviderBalanceRow({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [balance, setBalance] = useState(String(row.balance ?? ''));
  const [threshold, setThreshold] = useState(row.auto_reload_threshold ?? '');
  const [amount, setAmount] = useState(row.auto_reload_amount ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body = {
        auto_reload_threshold: threshold === '' ? null : threshold,
        auto_reload_amount: amount === '' ? null : amount
      };
      if (!row.live) body.balance = balance;   // never push over a live figure
      await apiClient.put(`/admin/financials/provider-balances/${row.provider}`, body);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const ageLabel = row.live
    ? 'live'
    : row.age_days === null ? 'never updated'
    : row.age_days < 1 ? 'updated today'
    : `updated ${Math.floor(row.age_days)}d ago`;

  return (
    <div className={`border rounded-lg p-4 ${row.stale ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 capitalize">{row.provider}</span>
            {row.live
              ? <Badge tone="blue">live</Badge>
              : <Badge tone={row.stale ? 'red' : 'gray'}>manual</Badge>}
            {row.stale && <Badge tone="red">stale</Badge>}
          </div>
          <p className={`text-xs mt-1 ${row.stale ? 'text-amber-700' : 'text-gray-500'}`}>
            {ageLabel}
            {row.auto_reload_threshold !== null && row.auto_reload_amount !== null && (
              <> · auto-reloads {money(row.auto_reload_amount)} below {money(row.auto_reload_threshold)}</>
            )}
          </p>
          {row.live_error && (
            <p className="text-xs text-amber-700 mt-1">
              Live read failed, showing last stored value: {row.live_error}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xl font-semibold text-gray-900">{money(row.balance)}</span>
          <button
            onClick={() => setEditing(!editing)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {editing ? 'Cancel' : 'Update'}
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field
            label="Balance"
            value={row.live ? row.balance : balance}
            onChange={setBalance}
            disabled={row.live}
            hint={row.live ? 'Read live from the provider' : 'From the provider console'}
          />
          <Field label="Auto-reload below" value={threshold} onChange={setThreshold} hint="Console-only, not readable via API" />
          <Field label="Auto-reload amount" value={amount} onChange={setAmount} hint="Console-only, not readable via API" />
          <div className="sm:col-span-3 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <span className="text-xs text-gray-500">Saving stamps the date automatically.</span>
            {err && <span className="text-sm text-red-600">{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, disabled, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

/* ── small presentational helpers ─────────────────────────────────────── */

function Th({ children, right }) {
  return (
    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

function Td({ children, right, strong }) {
  return (
    <td className={`px-6 py-4 whitespace-nowrap text-sm ${right ? 'text-right' : ''} ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
      {children}
    </td>
  );
}

const Dash = () => <span className="text-gray-300" title="Not applicable for this view">—</span>;

function Badge({ children, tone = 'blue' }) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-600'
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

function CategoryStat({ icon, label, value, pct, dot }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${dot}`} />
      <div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">{icon}{label}</div>
        <div className="text-2xl font-bold text-gray-900 mt-1">{money(value)}</div>
        {pct !== null && <div className="text-xs text-gray-500">{pct.toFixed(1)}% of variable spend</div>}
      </div>
    </div>
  );
}

/**
 * A metric we genuinely could not compute. Deliberately NOT a MetricCard showing
 * $0 — a fabricated zero here is a number someone acts on.
 */
function UnavailableCard({ label, icon, reason }) {
  return (
    <div className="bg-white rounded-lg shadow p-6 border border-amber-200">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600 mb-2">{label}</p>
          <p className="text-2xl font-bold text-amber-600">Unavailable</p>
          {reason && <p className="text-xs text-gray-500 mt-2 break-words">{reason}</p>}
        </div>
        <div className="ml-4 flex-shrink-0">
          <div className="rounded-lg p-3 bg-amber-50">{icon}</div>
        </div>
      </div>
    </div>
  );
}
